import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BookPlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { listReferenceBrands, createReferenceBrand, promoteTrainingCardToLibrary } from '../services/api';

/**
 * "📚 Add to library" button + dialog for the CertTrain screen.
 *
 * Engineer just labelled a card and confirmed the AI's reading. One
 * click on this button → pick a brand (or type a new one), confirm
 * the auto-suggested name → backend creates a `reference_items` row
 * with the card's submission as tags and the card's crop as the
 * photo. Zero double-entry between training and library.
 *
 * Disabled until the card has been labelled at least once
 * (`status='done'`) so we don't promote a still-pending card with
 * empty tags.
 */
const PromoteToLibraryButton = ({ card, currentForm, disabled }) => {
  const [open, setOpen] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState('');
  const [newBrandName, setNewBrandName] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Suggest a sensible default name from the card's labelled fields
  // so the engineer rarely has to type. e.g. `${brand} ${type}${rating}
  // ${ka} ${bsEn}` → 'Schneider C10 6kA 60898'.
  const suggestedName = useMemo(() => {
    const brandName = brands.find(b => b.id === brandId)?.name || newBrandName.trim();
    const type = (currentForm?.mcbType || '').toUpperCase();
    const rating = (currentForm?.mcbRating || '').replace(/[Aa]$/, '');
    const ka = (currentForm?.breakingCapacityKa || '').toUpperCase().replace(/KA$/, '').replace(/K$/, '').trim();
    const bsEn = (currentForm?.bsEn || '').replace(/^bs en\s*/i, '').replace(/^en\s*/i, '');
    const bits = [
      brandName,
      type && rating ? `${type}${rating}` : (rating || ''),
      ka ? `${ka}kA` : '',
      bsEn,
    ].filter(Boolean);
    return bits.join(' ').trim();
  }, [brands, brandId, newBrandName, currentForm]);

  useEffect(() => {
    if (open && !brands.length) {
      listReferenceBrands().then(setBrands).catch(() => setBrands([]));
    }
  }, [open, brands.length]);

  // Pre-fill the name input every time the dialog opens or the brand
  // changes — but only if the engineer hasn't already typed something
  // custom (i.e. the field is empty or matches the previous suggestion).
  const [lastSuggestion, setLastSuggestion] = useState('');
  useEffect(() => {
    if (open && (!name || name === lastSuggestion)) {
      setName(suggestedName);
      setLastSuggestion(suggestedName);
    }
  }, [open, suggestedName, name, lastSuggestion]);

  const isValid = !!card?.id && (brandId || newBrandName.trim()) && name.trim();

  const handleSave = async () => {
    if (!isValid) return;
    setSaving(true);
    try {
      let useBrandId = brandId;
      if (!useBrandId) {
        // Engineer typed a new brand name in the dialog — create it
        // first (idempotent on slug), then promote into it. This is
        // what makes the flow truly one-click for the common case
        // where the brand isn't in the library yet.
        const created = await createReferenceBrand(newBrandName.trim());
        useBrandId = created.id;
      }
      // Lift the engineer's LIVE form values (which include any
      // unsaved kA / type / rating edits made just before they hit
      // Add to library) and post them as `tagsOverride`. The backend
      // takes these in priority over `card.submission` / `aiGuess`,
      // so what the engineer sees on screen is what lands in the
      // library — no surprises from stale state.
      const tagsOverride = currentForm ? {
        mcbType: currentForm.mcbType || '',
        mcbRating: currentForm.mcbRating || '',
        rcdRating: currentForm.rcdRating || '',
        bsEn: currentForm.bsEn || '',
        breakingCapacityKa: currentForm.breakingCapacityKa || '',
      } : undefined;
      await promoteTrainingCardToLibrary(card.id, {
        brandId: useBrandId,
        name: name.trim(),
        notes: notes.trim() || undefined,
        tagsOverride,
      });
      toast.success('Saved to reference library');
      setOpen(false);
      setName('');
      setNotes('');
      setNewBrandName('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save to library');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={disabled || !card?.id}
        onClick={() => setOpen(true)}
        className="h-8 gap-1 text-emerald-700 border-emerald-300 hover:bg-emerald-50"
        title={disabled ? 'Label the card first' : 'Save this labelled crop as a reference library item'}
        data-testid="promote-to-library-btn"
      >
        <BookPlus className="h-3 w-3" />
        <span className="hidden sm:inline">Add to library</span>
        <span className="sm:hidden">Library</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Save this crop to the reference library</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Brand</Label>
              <Select value={brandId || 'new'} onValueChange={(v) => setBrandId(v === 'new' ? '' : v)}>
                <SelectTrigger data-testid="promote-brand-select"><SelectValue placeholder="Pick a brand or add new" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="new">+ New brand…</SelectItem>
                  {brands.map(b => {
                    const c = b.itemCounts || {};
                    const total = (c.breaker || 0) + (c.board || 0) + (c.isolator || 0);
                    return (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name} · {total} item{total === 1 ? '' : 's'}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {!brandId && (
                <Input
                  className="mt-2 text-sm h-8"
                  placeholder="e.g. Schneider"
                  value={newBrandName}
                  onChange={e => setNewBrandName(e.target.value)}
                  data-testid="promote-new-brand-input"
                />
              )}
            </div>
            <div>
              <Label className="text-xs">Name</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={suggestedName || 'e.g. Schneider C10 60898'}
                data-testid="promote-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. older yellow-handle revision"
              />
            </div>
            <p className="text-[11px] text-slate-500 leading-snug">
              The card's labelled tags and its crop image will be saved as the new library item — no double-entry.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={!isValid || saving} data-testid="promote-save-btn">
              {saving && <Loader2 className="h-3 w-3 animate-spin mr-2" />}
              Save to library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PromoteToLibraryButton;
